import BaseProduct from './BaseProduct';
import { ProductId } from '../types';
import store, { getLayerOpacity } from '../state';
import { getTileUrl } from '../config';
import { ClimateService } from '../services/ClimateService';

export class ClimateLayer extends BaseProduct {
  public readonly id: ProductId = 'climate';
  private unsubscribeStore: (() => void) | null = null;
  private lastDate: string = '';
  private lastViewMode: string = '';
  private lastVariable: string = '';
  private lastMonth: number = -1;
  private lastWeek: number = -1;

  protected onActivate(): void {
    this.refresh();
    this.unsubscribeStore = store.subscribe((s) => {
      if (!this.active) return;
      const defaultInfo = ClimateService.getDefaultDateInfo();
      const currentDate = s.climateSelectedDate || defaultInfo.dateStr;
      const currentMode = s.climateViewMode || 'daily';
      const currentVar = s.climateSelectedVariable || 'tmax_mean';
      const currentMonth = s.climateMonthIndex !== undefined ? s.climateMonthIndex : defaultInfo.monthIndex;
      const currentWeek = s.climateSelectedWeek || 11;

      if (
        currentDate !== this.lastDate ||
        currentMode !== this.lastViewMode ||
        currentVar !== this.lastVariable ||
        currentMonth !== this.lastMonth ||
        currentWeek !== this.lastWeek
      ) {
        this.refresh();
      }
    });
  }

  protected onDeactivate(): void {
    if (this.unsubscribeStore) {
      this.unsubscribeStore();
      this.unsubscribeStore = null;
    }
    this.layerManager.removeBufferedRasterLayer(this.id);
    this.layerManager.removeLayer(this.id);
    this.lastDate = '';
    this.lastViewMode = '';
    this.lastVariable = '';
    this.lastMonth = -1;
    this.lastWeek = -1;
  }

  protected onTimeUpdate(_date: string, _hour: string): void {
    this.refresh();
  }

  public refresh(): void {
    if (!this.active) return;
    const state = store.getState();
    const manifest = state.manifest;
    if (!manifest) return;

    const opacity = getLayerOpacity(this.id, 1.0);
    const defaultInfo = ClimateService.getDefaultDateInfo();
    const dateStr = state.climateSelectedDate || defaultInfo.dateStr;
    const viewMode = state.climateViewMode || 'daily';
    const selectedVar = state.climateSelectedVariable || 'tmax_mean';
    const monthIndex = state.climateMonthIndex !== undefined ? state.climateMonthIndex : defaultInfo.monthIndex;
    const selectedWeek = state.climateSelectedWeek || 11;

    this.lastDate = dateStr;
    this.lastViewMode = viewMode;
    this.lastVariable = selectedVar;
    this.lastMonth = monthIndex;
    this.lastWeek = selectedWeek;

    let timeKey = dateStr;
    if (viewMode === 'monthly') {
      timeKey = `m${String(monthIndex + 1).padStart(2, '0')}`;
    } else if (viewMode === 'weekly') {
      timeKey = `w${String(selectedWeek).padStart(2, '0')}`;
    }

    const tileUrl = getTileUrl(manifest, 'climate', selectedVar, timeKey, viewMode);
    if (!tileUrl) {
      this.layerManager.removeBufferedRasterLayer(this.id);
      this.layerManager.removeLayer(this.id);
      return;
    }

    this.layerManager.updateBufferedRasterLayer(this.id, [tileUrl], opacity, undefined, 0, 6);
  }
}

export default ClimateLayer;
